/**
 * React hook for offline sync functionality.
 * Provides sync status and methods to queue offline actions.
 */

import { useState, useEffect, useCallback } from 'react';
import { syncService, type SyncStatus } from '../lib/sync-service';
import { queueTimeEntry, type PendingTimeEntry } from '../lib/offline-store';

export interface UseOfflineSyncResult {
  /** Current sync status */
  status: SyncStatus;
  /** Queue a task action for offline sync */
  queueAction: (
    taskId: string,
    action: PendingTimeEntry['action']
  ) => Promise<string>;
  /** Manually trigger sync */
  triggerSync: () => Promise<void>;
}

/**
 * Generate a UUID v4 for client entry ID.
 */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function useOfflineSync(): UseOfflineSyncResult {
  const [status, setStatus] = useState<SyncStatus>(syncService.getStatus());

  useEffect(() => {
    // Subscribe to sync status changes
    const unsubscribe = syncService.subscribe(setStatus);

    // Initial pending count refresh
    syncService.refreshPendingCount();

    return unsubscribe;
  }, []);

  const queueAction = useCallback(
    async (
      taskId: string,
      action: PendingTimeEntry['action']
    ): Promise<string> => {
      const clientEntryId = generateUUID();
      const deviceTimestamp = new Date().toISOString();

      await queueTimeEntry({
        clientEntryId,
        taskId,
        action,
        deviceTimestamp,
      });

      // Refresh pending count
      await syncService.refreshPendingCount();

      // If online, trigger sync immediately
      if (status.isOnline) {
        syncService.triggerSync();
      }

      return clientEntryId;
    },
    [status.isOnline]
  );

  const triggerSync = useCallback(async () => {
    await syncService.triggerSync();
  }, []);

  return {
    status,
    queueAction,
    triggerSync,
  };
}

/**
 * Hook to get just the online status.
 */
export function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}
